import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adminService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const ChangePasswordDialog = ({ open, onClose, admin, onSuccess }) => {
  const { t } = useTranslation();
  const { admin: currentAdmin } = useAuth();
  const isOwnPassword = !admin || admin._id === currentAdmin?.id || admin.id === currentAdmin?.id;

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (isOwnPassword && !formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = t('admins.passwordRequirements');
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('admins.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setAlert(null);

    try {
      if (isOwnPassword) {
        await adminService.changeOwnPassword({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        });
      } else {
        await adminService.changePassword(admin._id || admin.id, {
          newPassword: formData.newPassword
        });
      }

      onSuccess(t('admins.passwordChanged'));
      onClose();
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.error || 'Failed to change password';
      setAlert({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    setAlert(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isOwnPassword ? t('admins.changeOwnPassword') : t('admins.changePassword')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {alert && (
            <Alert severity={alert.type}>
              {alert.message}
            </Alert>
          )}

          {!isOwnPassword && admin && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2">
                Changing password for:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {admin.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {admin.email}
              </Typography>
            </Box>
          )}

          {isOwnPassword && (
            <TextField
              label={t('admins.currentPassword')}
              type="password"
              value={formData.currentPassword}
              onChange={handleChange('currentPassword')}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              fullWidth
              required
              disabled={loading}
              autoFocus
            />
          )}

          <TextField
            label={t('admins.newPassword')}
            type="password"
            value={formData.newPassword}
            onChange={handleChange('newPassword')}
            error={!!errors.newPassword}
            helperText={errors.newPassword || t('admins.passwordRequirements')}
            fullWidth
            required
            disabled={loading}
            autoFocus={!isOwnPassword}
          />

          <TextField
            label={t('admins.confirmNewPassword')}
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            fullWidth
            required
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? t('common.saving') : t('admins.changePassword')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;